/*
 * tq-header — expanding header search (<tq-header-search>).
 *
 * The markup (snippets/tq-header.liquid) is a native <details>: without JS,
 * clicking the magnifier simply opens a plain search form below the header
 * that submits to /search. This element upgrades it:
 *
 *   - Opening: the bar grows out of the magnifier. On desktop it stretches left
 *     up to the logo while the nav fades out; on mobile (≤900px) it covers the
 *     whole header row. The geometry is measured here and handed to CSS as
 *     custom properties on .tq-head; the animation itself is a clip-path
 *     transition in tq-header.css, driven by the `.is-searching` class.
 *   - Closing: ✕, Escape, a click outside, or focus leaving an empty field.
 *     `.is-searching` goes first so the reverse animation plays, then the
 *     <details> is closed once it has finished.
 *   - Live suggestions: products from the predictive search JSON endpoint
 *     (/search/suggest.json), searching the same fields as the /search page's
 *     predictive-search.js (incl. tags + body, so "bio" / "organic" match).
 *     Rendered as a listbox with ↑/↓/Enter keyboard support.
 *
 * Self-contained on purpose: global.js (debounce, fetchConfig…) is not loaded
 * on the home / collection templates, so nothing here depends on it.
 */

class TqHeaderSearch extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.input = this.querySelector('.tq-search__input');
    this.form = this.querySelector('.tq-search__form');
    this.closeButton = this.querySelector('.tq-search__close');
    this.results = this.querySelector('.tq-search__results');
    this.status = this.querySelector('.tq-search__status');
    this.head = this.closest('.tq-head');
    this.wrap = this.closest('.wrap');
    this.logo = this.head.querySelector('.logo');

    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;
    this.closeTimer = null;
    this.activeIndex = -1;
    this.mobileQuery = window.matchMedia('(max-width: 900px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.summary.setAttribute('aria-expanded', 'false');
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.isOpen() ? this.close() : this.open();
    });
    this.closeButton.addEventListener('click', () => this.close({ focusToggle: true }));
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.addEventListener('focusout', () => this.onFocusOut());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close({ focusToggle: true });
      }
    });
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen() && !this.contains(event.target)) this.close();
    });
    window.addEventListener('resize', () => {
      if (this.isOpen()) this.measure();
    });
  }

  isOpen() {
    return this.head.classList.contains('is-searching');
  }

  /* Hands the bar's target box to CSS. Desktop: from just right of the logo
     to the magnifier's right edge. Mobile: the full header row (CSS ignores
     --tq-s-left / --tq-s-right there). In both cases --tq-s-from is the
     magnifier's distance from the bar's right edge, so the closed clip-path
     sits exactly on the icon and the bar visibly grows out of it. */
  measure() {
    const wrapBox = this.wrap.getBoundingClientRect();
    const iconBox = this.summary.getBoundingClientRect();
    const styles = getComputedStyle(this.wrap);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    const gap = parseFloat(styles.columnGap) || 0;

    let left = padLeft;
    let right = padRight;
    if (!this.mobileQuery.matches) {
      left = this.logo.getBoundingClientRect().right - wrapBox.left + gap;
      right = wrapBox.right - iconBox.right;
    }
    const from = Math.max(0, wrapBox.right - right - iconBox.right);

    this.head.style.setProperty('--tq-s-left', `${Math.round(left)}px`);
    this.head.style.setProperty('--tq-s-right', `${Math.round(right)}px`);
    this.head.style.setProperty('--tq-s-from', `${Math.round(from)}px`);
    this.head.style.setProperty('--tq-s-icon', `${Math.round(iconBox.width)}px`);
  }

  open() {
    clearTimeout(this.closeTimer);
    this.head.querySelector('.nav-mob[open]')?.removeAttribute('open');
    this.measure();
    this.details.open = true;
    // Commit the collapsed state before switching to the open one, so the
    // clip-path transition has a start value (a forced reflow rather than
    // requestAnimationFrame, which never fires in background tabs).
    void this.form.offsetWidth;
    this.head.classList.add('is-searching');
    this.summary.setAttribute('aria-expanded', 'true');
    this.input.focus({ preventScroll: true });
    if (this.input.value.trim()) this.onInput();
  }

  close({ focusToggle = false } = {}) {
    if (!this.isOpen()) return;
    this.head.classList.remove('is-searching');
    this.summary.setAttribute('aria-expanded', 'false');
    this.hideResults();
    if (focusToggle) this.summary.focus({ preventScroll: true });
    const delay = this.reducedMotion.matches ? 0 : 340;
    this.closeTimer = setTimeout(() => {
      if (!this.isOpen()) this.details.open = false;
    }, delay);
  }

  onFocusOut() {
    // Wait for the new focus target to settle before deciding.
    setTimeout(() => {
      if (!this.isOpen() || this.contains(document.activeElement)) return;
      if (this.input.value.trim()) {
        this.hideResults();
      } else {
        this.close();
      }
    });
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();
    if (!query) {
      this.abortController?.abort();
      this.hideResults();
      return;
    }
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 250);
  }

  async fetchResults(query) {
    if (this.cache.has(query)) {
      this.render(query, this.cache.get(query));
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();

    const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
    });

    try {
      const response = await fetch(`${base}.json?${params}`, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(response.status);
      const data = await response.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      this.cache.set(query, products);
      // The field may have changed while the request was in flight.
      if (this.input.value.trim() === query) this.render(query, products);
    } catch (error) {
      if (error.name !== 'AbortError') this.hideResults();
    }
  }

  render(query, products) {
    if (!this.isOpen()) return;
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');

    const items = products.map((product, index) => {
      const li = document.createElement('li');
      li.className = 'tq-search__item';
      li.id = `TqSearchOption-${index}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');

      const link = document.createElement('a');
      link.href = product.url;
      link.tabIndex = -1;

      const media = document.createElement('span');
      media.className = 'tq-search__thumb';
      const imageUrl = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = this.sizedImage(imageUrl, 96);
        img.alt = '';
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        media.append(img);
      }

      const text = document.createElement('span');
      text.className = 'tq-search__text';
      const title = document.createElement('span');
      title.className = 'tq-search__title';
      title.textContent = product.title;
      const meta = document.createElement('span');
      meta.className = 'tq-search__meta';
      meta.textContent = this.priceLabel(product);
      if (!product.available) {
        const badge = document.createElement('span');
        badge.className = 'tq-search__badge';
        badge.textContent = this.dataset.soldOut;
        meta.append(badge);
      }
      text.append(title, meta);

      link.append(media, text);
      li.append(link);
      return li;
    });

    const searchUrl = `${this.form.getAttribute('action')}?${new URLSearchParams({ q: query, 'options[prefix]': 'last' })}`;
    const all = document.createElement('li');
    all.className = 'tq-search__item tq-search__all';
    all.id = `TqSearchOption-${items.length}`;
    all.setAttribute('role', 'option');
    all.setAttribute('aria-selected', 'false');
    const allLink = document.createElement('a');
    allLink.href = searchUrl;
    allLink.tabIndex = -1;
    allLink.textContent = (products.length ? this.dataset.viewAll : this.dataset.noResults).replace('[terms]', query);
    all.append(allLink);

    this.results.replaceChildren(...items, all);
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.status.textContent = products.length
      ? this.dataset.count.replace('[count]', products.length)
      : this.dataset.noResults.replace('[terms]', query);
  }

  hideResults() {
    this.results.hidden = true;
    this.results.replaceChildren();
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  onKeydown(event) {
    const options = Array.from(this.results.querySelectorAll('[role="option"]'));
    if (this.results.hidden || !options.length) return;
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    this.activeIndex = (this.activeIndex + step + options.length + 1) % (options.length + 1);
    if (this.activeIndex === options.length) this.activeIndex = -1; // back to the field
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
    if (this.activeIndex === -1) {
      this.input.removeAttribute('aria-activedescendant');
    } else {
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  onSubmit(event) {
    if (!this.input.value.trim()) {
      event.preventDefault();
      return;
    }
    const active = this.results.querySelector('[aria-selected="true"] a');
    if (active) {
      event.preventDefault();
      window.location.href = active.href;
    }
  }

  priceLabel(product) {
    const min = parseFloat(product.price_min || product.price);
    if (Number.isNaN(min)) return '';
    const max = parseFloat(product.price_max || product.price);
    const price = this.formatMoney(min);
    return max > min ? this.dataset.priceFrom.replace('[price]', price) : price;
  }

  formatMoney(amount) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
    const lang = (document.documentElement.lang || 'fr').split('-')[0];
    const country = window.Shopify && Shopify.country;
    try {
      return new Intl.NumberFormat(country ? `${lang}-${country}` : lang, { style: 'currency', currency }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  sizedImage(url, width) {
    try {
      const sized = new URL(url, window.location.origin);
      sized.searchParams.set('width', width);
      return sized.toString();
    } catch (error) {
      return url;
    }
  }
}

if (!customElements.get('tq-header-search')) customElements.define('tq-header-search', TqHeaderSearch);
